import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import axiosClient from '@/api/axiosClient';
import { addTableRows, updateTableCell } from '@/api/endpoints/tableData';
import { exportDataView, getDataViewExportOptions } from '@/api/endpoints/dataViews';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useQueryStore } from '@/stores/queryStore';
import { downloadBlob } from '@/utils/download';
import type { DataViewExportOptionsResponse } from '@/api/contracts/dataModel';
import { wireText } from '@/helpers/wireText';

type UnknownRecord = Record<string, unknown>;

type ExportErrorWithResponse = {
  response?: { data?: { detail?: string; title?: string } };
  message?: string;
};

function normalizeTableKey (value: unknown): string
{
  return wireText(value).trim();
}

function normalizeProblemErrors (problemDetails: UnknownRecord | null | undefined): string[]
{
  const errors = problemDetails?.errors;
  if (Array.isArray(errors)) return errors.map(String);
  if (typeof problemDetails?.detail === 'string') return [problemDetails.detail];
  if (typeof problemDetails?.title === 'string') return [problemDetails.title];
  return [];
}

function rowIdMatches (rowIds: Array<number | string>, targetId: unknown): boolean
{
  const targetStr = String(targetId);
  return rowIds.some((id) => String(id) === targetStr);
}

function forceFileDownload (data: BlobPart, fileName: string, contentType: string | null = null): void
{
  const blob = contentType ? new Blob([data], { type: contentType }) : new Blob([data]);
  downloadBlob(blob, fileName);
}

async function loadExportOptions (exportId: string): Promise<DataViewExportOptionsResponse | null>
{
  try {
    return await getDataViewExportOptions(exportId);
  } catch (error) {
    // Silent swallow: preflight is a helper query. On failure the UI falls back
    // to defaults (hasMainTable=true). No notification to the user.
    console.warn('Kunne ikke hente eksport-options:', error);
    return null;
  }
}

export const useTableStore = defineStore('tableStore', () =>
{
  const notificationsStore = useNotificationsStore();
  const queryStore = useQueryStore();

  const pagedResult = ref<UnknownRecord>({});
  const totalRowsInTable = ref<number | null>(null);

  const downloading = ref(false);
  const exportProgress = ref<UnknownRecord | null>(null);
  const activeExportTaskId = ref<string | null>(null);

  const tableKey = computed<string>(() =>
    normalizeTableKey(
      queryStore.mainTableKey
      ?? queryStore.currentView?.mainTable?.tableKey
    )
  );

  function requireTableKey (overrideTableKey: unknown = null): string
  {
    const resolved = normalizeTableKey(overrideTableKey || tableKey.value);
    if (!resolved) throw new Error('Mangler tableKey for tabelloperasjon.');
    return resolved;
  }

  async function applyUpdateToLocalState (
    payload: { rowIds: Array<number | string>; columnName: string; newValue: unknown },
    updatedRows: number
  ): Promise<void>
  {
    const items = (pagedResult.value?.items as UnknownRecord[] | undefined) ?? null;
    if (updatedRows <= 0 || !items) return;

    const updatedItems = items.map((item) =>
      rowIdMatches(payload.rowIds, item.icsRowId_)
        ? { ...item, [payload.columnName]: payload.newValue }
        : item
    );

    pagedResult.value = { ...pagedResult.value, items: updatedItems };

    notificationsStore.addSuccess('Lagret', `Kolonne ${payload.columnName} ble endret på ${updatedRows} rader`);
  }

  async function saveData (params: {
    tableKey?: string;
    rowId: number | string;
    columnName: string;
    oldValue: unknown;
    newValue: unknown;
  }): Promise<boolean>
  {
    try {
      const { tableKey: overrideTableKey, rowId, columnName, oldValue, newValue } = params;
      if (oldValue === newValue) return false;
      if (!rowId) return false;

      const resolvedTableKey = requireTableKey(overrideTableKey);
      const rowIds = [Number(rowId)];
      const result = await updateTableCell(resolvedTableKey, {
        rowIds,
        columnKey: columnName,
        newValue
      });

      await applyUpdateToLocalState({ rowIds, columnName, newValue }, result.updatedRows);
      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Ukjent feil ved lagring';
      console.error('[TableStore] saveData failed', { params, error });
      notificationsStore.addError('Lagring feilet', message);
      throw error;
    }
  }

  async function addRecord (params: {
    tableKey?: string;
    inputValues: UnknownRecord | UnknownRecord[];
  }): Promise<{ success: boolean; errors?: string[]; createdRowIds?: number[] }>
  {
    const { tableKey: overrideTableKey, inputValues } = params;
    const resolvedTableKey = requireTableKey(overrideTableKey);
    const rows = Array.isArray(inputValues) ? inputValues : [inputValues];

    try {
      const response = await addTableRows(resolvedTableKey, { rows });
      notificationsStore.addSuccess('Rad opprettet', 'Rad ble opprettet');
      return { success: true, createdRowIds: response.createdRowIds };
    } catch (error: unknown) {
      const e = error as { response?: { status?: number; data?: UnknownRecord } };
      if (e?.response?.status === 400) {
        return { success: false, errors: normalizeProblemErrors(e.response.data) };
      }
      throw error;
    }
  }

  async function addRecords (params: {
    tableKey?: string;
    inputValues: UnknownRecord[];
    fieldActions?: Record<string, string>;
  }): Promise<{ success: boolean; errors?: string[] }>
  {
    const { tableKey: overrideTableKey, inputValues, fieldActions } = params;
    const resolvedTableKey = requireTableKey(overrideTableKey);

    try {
      await addTableRows(resolvedTableKey, { rows: inputValues, fieldActions });
      const rowCount = inputValues.length;
      const message = rowCount === 1 ? 'Rad opprettet' : `${rowCount} rader opprettet`;
      notificationsStore.addSuccess(message, message);
      return { success: true };
    } catch (error: unknown) {
      const e = error as { response?: { status?: number; data?: UnknownRecord } };
      if (e?.response?.status === 400) {
        return { success: false, errors: normalizeProblemErrors(e.response.data) };
      }
      throw error;
    }
  }

  async function updateExportProgress (progress: UnknownRecord): Promise<void>
  {
    const progressAny = progress as Record<string, unknown>;
    const normalizedStatus = wireText(progressAny?.status || '').toLowerCase();
    const normalizedProgress = { ...progressAny, status: normalizedStatus } as Record<string, unknown>;
    exportProgress.value = normalizedProgress;

    if (normalizedStatus === 'cancelled') {
      downloading.value = false;
      notificationsStore.addWarn('Eksport avbrutt', 'Eksporten ble avbrutt av bruker');
      if (wireText(normalizedProgress.exportId) === wireText(activeExportTaskId.value)) {
        activeExportTaskId.value = null;
      }
      return;
    }

    if (normalizedStatus === 'completed') {
      const receivedId = normalizedProgress.exportId != null ? wireText(normalizedProgress.exportId) : null;
      const activeId = activeExportTaskId.value != null ? wireText(activeExportTaskId.value) : null;
      if (receivedId !== activeId) return;

      await downloadBlob({
        exportId: normalizedProgress.exportId,
        contentType: normalizedProgress.contentType,
        name: normalizedProgress.name
      });
      downloading.value = false;
      activeExportTaskId.value = null;
      return;
    }

    if (normalizedStatus === 'error') {
      if (wireText(normalizedProgress.exportId) === wireText(activeExportTaskId.value)) {
        activeExportTaskId.value = null;
        downloading.value = false;
      }
      return;
    }

    if (normalizedStatus === 'running') {
      downloading.value = true;
    }
  }

  async function cancelExport (taskId: string): Promise<void>
  {
    try {
      if (exportProgress.value) {
        exportProgress.value = {
          ...exportProgress.value,
          status: 'cancelling',
          message: 'Avbryter eksport...'
        };
      }

      const response = await axiosClient.post(
        '/v2/export/cancel',
        { taskId },
        {
          responseType: 'json',
          validateStatus: (status) => status < 500
        }
      );

      if (response.status === 200) {
        notificationsStore.addSuccess('Avbryt forespurt', 'Forespørsel om å avbryte eksport er sendt');
        return;
      }

      notificationsStore.addError('Avbryt feilet', response.data?.message || 'Kunne ikke avbryte eksport');
      if (exportProgress.value) {
        exportProgress.value = {
          ...exportProgress.value,
          status: 'running',
          message: ''
        };
      }
    } catch {
      notificationsStore.addError('Avbryt feilet', 'Kunne ikke avbryte eksport');
      if (exportProgress.value) {
        exportProgress.value = {
          ...exportProgress.value,
          status: 'running',
          message: ''
        };
      }
    }
  }

  async function startExport (
    exportId: string,
    request: UnknownRecord
  ): Promise<string | null>
  {
    downloading.value = true;
    try {
      const result = await exportDataView(exportId, request as never);
      if (result.taskId) {
        activeExportTaskId.value = result.taskId;
        return result.taskId;
      }
      downloading.value = false;
      notificationsStore.addError('Eksport feilet', 'Kunne ikke starte eksport.');
      return null;
    } catch (error) {
      downloading.value = false;
      const ax = error as ExportErrorWithResponse;
      const message = ax?.response?.data?.detail ?? ax?.response?.data?.title ?? ax?.message ?? 'Kunne ikke starte eksport.';
      notificationsStore.addError('Eksport feilet', message);
      return null;
    }
  }

  async function downloadBlob (params: { exportId: unknown; contentType: unknown; name: unknown }): Promise<void>
  {
    const { exportId, contentType, name } = params;
    // The id goes into the request path, so it is read as text first. A value that is not text has
    // no place in a URL - it would have built /v2/export/[object Object] and asked the server for
    // something that cannot exist. Reading it first makes the guard below catch that too.
    const exportPath = wireText(exportId);
    if (!exportPath) {
      notificationsStore.addError('Nedlastingsfeil', 'Kunne ikke hente eksporterte filer.');
      return;
    }

    try {
      const response = await axiosClient.get(`/v2/export/${exportPath}`, { responseType: 'blob' });
      forceFileDownload(response.data, wireText(name || 'export'), contentType ? wireText(contentType) : null);
    } catch {
      notificationsStore.addError('Nedlastingsfeil', 'Kunne ikke laste ned filen.');
    }
  }

  return {
    pagedResult,
    totalRowsInTable,
    tableKey,
    downloading,
    exportProgress,
    activeExportTaskId,
    saveData,
    addRecord,
    addRecords,
    updateExportProgress,
    cancelExport,
    loadExportOptions,
    startExport,
    downloadBlob
  };
});
